import axios from 'axios';

// Helper to ensure URL uses HTTPS if requested or if on a secure origin
const ensureSecureUrl = (url) => {
  if (!url) return url;
  if (typeof window !== 'undefined' && window.location.protocol === 'https:' && url.startsWith('http://')) {
    // Only upgrade if not localhost to avoid breaking local dev
    if (!url.includes('localhost') && !url.includes('127.0.0.1')) {
      return url.replace('http://', 'https://');
    }
  }
  return url;
};

// Use environment variable, fallback to localhost for local dev only if env is missing
const rawBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
export const API_BASE_URL = ensureSecureUrl(rawBaseUrl);
export const BASE_SERVER_URL = API_BASE_URL.replace('/api', ''); // For static assets like images

// Helper to get full image URL
// Helper to get full image URL
export const getImageUrl = (path) => {
  if (!path || typeof path !== 'string') return '';
  if (path.startsWith('http')) return path; // Already a full URL
  const cleanPath = path.startsWith('/') ? path.substring(1) : path;
  return `${BASE_SERVER_URL}/${cleanPath}`;
};

const API_URL = API_BASE_URL;

const isDevApiLog =
  process.env.NODE_ENV === 'development' || process.env.REACT_APP_DEBUG_API === 'true';
if (isDevApiLog) {
  console.log('API Base URL:', API_URL);
}

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout
});

// Add token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}, (error) => {
  console.error('Request error:', error);
  return Promise.reject(error);
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    if (isDevApiLog) {
      console.log(
        `API Response: ${response.config.method?.toUpperCase()} ${response.config.url} - Status: ${response.status}`
      );
    }
    return response;
  },
  (error) => {
    console.error('API Error:', {
      message: error.message,
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      baseURL: error.config?.baseURL
    });

    if (error.code === 'ECONNREFUSED' || error.message.includes('Network Error')) {
      console.error('❌ Backend server is not running or not accessible');
      console.error('   Please ensure the backend is running on http://localhost:5000');
    }

    return Promise.reject(error);
  }
);



export const productsAPI = {
  getAll: (params) => api.get('/products', { params }),
  getById: (id) => api.get(`/products/${id}`),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`),
};

// Categories API
export const categoriesAPI = {
  getAll: (params) => api.get('/categories', { params }),
  getById: (id) => api.get(`/categories/${id}`),
  getBySlug: (slug) => api.get(`/categories/slug/${slug}`),
  create: (data) => api.post('/categories', data),
  update: (id, data) => api.put(`/categories/${id}`, data),
  delete: (id) => api.delete(`/categories/${id}`),
};

// Blog API
export const blogAPI = {
  getAll: (params) => api.get('/blog', { params }),
  getById: (id) => api.get(`/blog/${id}`),
  getBySlug: (slug) => api.get(`/blog/slug/${slug}`),
  create: (data) => api.post('/blog', data),
  update: (id, data) => api.put(`/blog/${id}`, data),
  delete: (id) => api.delete(`/blog/${id}`),
};

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
};

// Admin API
export const adminAPI = {
  getStats: () => api.get('/admin/stats'),
  clearData: (password) => api.post('/admin/clear-data', { password }),
  seedData: (password, reset = false) => api.post('/admin/seed-data', { password, reset }),
};

// Enquiries API
export const enquiriesAPI = {
  create: (data) => api.post('/enquiries', data),
  getAll: () => api.get('/enquiries'),
};

// Users API
export const usersAPI = {
  getAll: () => api.get('/users'),
  updateRole: (id, role) => api.put(`/users/${id}/role`, { role }),
  delete: (id) => api.delete(`/users/${id}`),
};

// Certificates API
export const certificatesAPI = {
  getAll: () => api.get('/certificates'),
  create: (data) => api.post('/certificates', data),
  update: (id, data) => api.put(`/certificates/${id}`, data),
  delete: (id) => api.delete(`/certificates/${id}`),
};

export default api;

